import { useCallback, useState } from 'react'

import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { ApproverCandidate } from '@ebalance/proto/bffuser/v1/approval_flow_model_pb'
import { useTranslation } from 'react-i18next'

import { StageEditModal } from '~/components/approval/StageEditModal'
import { type StageLocal } from '~/components/approval/stage'
import {
  AREA_CODE_LABELS,
  type CellKey,
  type ProcessDef,
} from '~/pages/AdminApprovalFlow/constants/processMap'
import {
  type MatrixAction,
  type ProcessState,
} from '~/pages/AdminApprovalFlow/models/reducer'

type ModalState =
  | { type: 'none' }
  | {
      type: 'stage'
      cellKey: CellKey
      areaCode: AreaCode
    }

/** 表描画本体 (`ApprovalMatrix`) の click ハンドラから呼ぶ open callback 群。 */
export type MatrixModalControls = {
  openStage: (args: { cellKey: CellKey; areaCode: AreaCode }) => void
}

type Props = {
  processDef: ProcessDef
  processState: ProcessState
  candidates: ApproverCandidate[]
  dispatch: (action: MatrixAction) => void
  processLabel: string
  children: (controls: MatrixModalControls) => React.ReactNode
}

/**
 * ApprovalMatrix の承認段階編集モーダルを持つ host。
 *
 * modal state (`ModalState`) と JSX をここに持ち、
 * 表描画本体には open callback (`children` render prop) だけを渡す。
 */
export const ApprovalMatrixModalHost = ({
  processDef,
  processState,
  candidates,
  dispatch,
  processLabel,
  children,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [modalState, setModalState] = useState<ModalState>({ type: 'none' })

  const openStage = useCallback(
    (args: { cellKey: CellKey; areaCode: AreaCode }) => {
      setModalState({ type: 'stage', ...args })
    },
    [],
  )

  const handleStageDone = useCallback(
    (stages: StageLocal[]) => {
      if (modalState.type !== 'stage') return
      dispatch({
        type: 'SET_CELL_STAGES',
        businessProcess: processDef.businessProcess,
        key: modalState.cellKey,
        stages,
      })
      setModalState({ type: 'none' })
    },
    [dispatch, modalState, processDef.businessProcess],
  )

  const handleCopyToOtherAreas = useCallback(
    (stages: StageLocal[], destAreas: AreaCode[]) => {
      if (modalState.type !== 'stage') return
      dispatch({
        type: 'SET_CELL_STAGES',
        businessProcess: processDef.businessProcess,
        key: modalState.cellKey,
        stages,
      })
      dispatch({
        type: 'COPY_TO_OTHER_AREAS',
        businessProcess: processDef.businessProcess,
        srcKey: modalState.cellKey,
        // ユーザーがチェックボックスで選択した先のみに上書きする (AC-023)
        destAreas,
      })
      setModalState({ type: 'none' })
    },
    [dispatch, modalState, processDef.businessProcess],
  )

  const modalCell =
    modalState.type === 'stage'
      ? processState.cells.get(modalState.cellKey)
      : undefined

  return (
    <>
      {children({ openStage })}

      {modalState.type === 'stage' && (
        <StageEditModal
          candidates={candidates}
          initialStages={modalCell?.stages ?? []}
          isOpen
          onClose={() => setModalState({ type: 'none' })}
          onDone={handleStageDone}
          onReflectToOtherAreas={handleCopyToOtherAreas}
          reflectAreas={{
            areas: processDef.areas,
            sourceAreaCode: modalState.areaCode,
            areaLabels: AREA_CODE_LABELS,
          }}
          title={t('page:adminApprovalFlow.stageModal.titleFormat', {
            process: processLabel,
            area: AREA_CODE_LABELS[modalState.areaCode],
          })}
        />
      )}
    </>
  )
}
